/**
 * API Service for System Administration Portal
 * Handles all backend communication
 */

import axios from 'axios';
import { getTenantFromSubdomain } from './tenant';
import {
  forceSessionExpired,
  isSessionRedirectInProgress,
  reasonFromAuthError,
} from './sessionExpiry';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

// X-Tenant-ID must be a tenant UUID when sent. Subdomain slugs are NOT valid here.
const TENANT_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and tenant header to requests
api.interceptors.request.use((config) => {
  // Report authoring passes an explicit target UUID via xt(). Keep that header.
  // On localhost, getTenantFromSubdomain() is null (scope is ?scope=, not
  // ?tenant=), and wiping the header made preview/run look up the spec as the
  // platform tenant → 404 Report spec not found.
  const existing = config.headers?.['X-Tenant-ID'] || config.headers?.['x-tenant-id'];
  if (!(existing && TENANT_UUID_RE.test(String(existing)))) {
    const tenant = getTenantFromSubdomain();
    if (tenant && TENANT_UUID_RE.test(tenant)) {
      config.headers['X-Tenant-ID'] = tenant;
    } else if (config.headers) {
      delete config.headers['X-Tenant-ID'];
      delete config.headers['x-tenant-id'];
    }
  }
  
  // Add auth token if available
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const cfg = error.config || {};
    if (error.response?.status === 401) {
      const isAuthEndpoint = !!cfg.url && (
        cfg.url.includes('/auth/login') ||
        cfg.url.includes('/auth/verify-2fa') ||
        cfg.url.includes('/auth/enroll-2fa') ||
        cfg.url.includes('/auth/profile') ||
        cfg.url.includes('/auth/me')
      );
      if (!cfg.skipAuthRedirect && !isAuthEndpoint && !isSessionRedirectInProgress()) {
        forceSessionExpired(reasonFromAuthError(error), error.response?.data?.message);
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// AUTHENTICATION
// ============================================================================

export const authService = {
  login: async (email, password) => {
    const tenantSlug = getTenantFromSubdomain();
    const response = await api.post('/auth/login', { email, password, tenant_slug: tenantSlug });
    return response.data;
  },

  verify2FA: async (twoFactorToken, code) => {
    const response = await api.post('/auth/verify-2fa', { twoFactorToken, code });
    return response.data;
  },

  enroll2FA: async (enrollToken, code) => {
    const response = await api.post('/auth/enroll-2fa/verify', { enrollToken, code });
    return response.data;
  },

  getProfile: async () => {
    // skipAuthRedirect: this is the init-time profile check; a 401 just means
    // "not signed in" and the App.js handler clears the token itself. Without
    // this flag we would bounce the user to /admin?reason=session_expired on
    // every fresh visit with a stale token, even though the App is already
    // about to render the LoginView.
    const response = await api.get('/auth/profile', { skipAuthRedirect: true });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('admin_token');
  },
};

// ============================================================================
// TENANTS
// ============================================================================

export const tenantsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/tenants', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/tenants/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/tenants', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/tenants/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/tenants/${id}`);
    return response.data;
  },

  getStats: async (id) => {
    const response = await api.get(`/admin/tenants/${id}/stats`);
    return response.data;
  },

  getUsers: async (id) => {
    const response = await api.get(`/admin/tenants/${id}/users`);
    return response.data;
  },

  addUser: async (id, userData) => {
    const response = await api.post(`/admin/tenants/${id}/users`, userData);
    return response.data;
  },

  updateUser: async (id, userId, userData) => {
    const response = await api.put(`/admin/tenants/${id}/users/${userId}`, userData);
    return response.data;
  },

  getAddons: async (id) => {
    const response = await api.get(`/admin/tenants/${id}/addons`);
    return response.data;
  },

  grantAddon: async (id, slug) => {
    const response = await api.post(`/admin/tenants/${id}/addons/${slug}/grant`);
    return response.data;
  },

  revokeAddon: async (id, slug) => {
    const response = await api.post(`/admin/tenants/${id}/addons/${slug}/revoke`);
    return response.data;
  },

  getModules: async (id) => {
    const response = await api.get(`/admin/tenants/${id}/modules`);
    return response.data;
  },

  updateModule: async (id, slug, data) => {
    const response = await api.put(`/admin/tenants/${id}/modules/${slug}`, data);
    return response.data;
  },

  previewResetData: async (id) => {
    const response = await api.post(`/admin/tenants/${id}/reset-data`, {});
    return response.data;
  },

  resetData: async (id, { confirm_name, totp_code }) => {
    const response = await api.post(
      `/admin/tenants/${id}/reset-data`,
      { dry_run: false, confirm_name, totp_code },
      { timeout: 180000 }
    );
    return response.data;
  },
};

// ============================================================================
// ADD-ON CATALOG (platform list prices; super_admin)
// ============================================================================

export const addonCatalogService = {
  getAll: async () => {
    const response = await api.get('/admin/addon-catalog');
    return response.data;
  },

  updatePrice: async (slug, priceCents) => {
    const response = await api.put(`/admin/addon-catalog/${slug}`, {
      price_cents: priceCents,
    });
    return response.data;
  },
};

// ============================================================================
// SUBSCRIPTION PLANS CATALOG (bundles; super_admin)
// ============================================================================

export const subscriptionPlansAdminService = {
  list: async (params = {}) => {
    const response = await api.get('/admin/subscription-plans', { params });
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/subscription-plans', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/subscription-plans/${id}`, data);
    return response.data;
  },
};

// ============================================================================
// SIGNUP REQUESTS (platform prospect queue)
// ============================================================================

export const signupRequestsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/signup-requests', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/signup-requests/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.patch(`/admin/signup-requests/${id}`, data);
    return response.data;
  },

  sendEmail: async (id, data) => {
    const response = await api.post(`/admin/signup-requests/${id}/send-email`, data);
    return response.data;
  },

  getEmails: async (id) => {
    const response = await api.get(`/admin/signup-requests/${id}/emails`);
    return response.data;
  },
};

// ============================================================================
// EMAIL TEMPLATES (platform tenant scope in onboarding admin)
// ============================================================================

export const emailTemplatesService = {
  getMergeFields: async (purpose) => {
    const response = await api.get('/admin/email-templates/merge-fields', {
      params: purpose ? { purpose } : {},
    });
    return response.data;
  },

  getAll: async (params = {}) => {
    const response = await api.get('/admin/email-templates', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/email-templates/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/email-templates', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/email-templates/${id}`, data);
    return response.data;
  },

  remove: async (id) => {
    const response = await api.delete(`/admin/email-templates/${id}`);
    return response.data;
  },

  previewAudience: async (data) => {
    const response = await api.post('/admin/email-templates/preview-audience', data);
    return response.data;
  },

  sendToAudience: async (id, data) => {
    const response = await api.post(`/admin/email-templates/${id}/send`, data);
    return response.data;
  },
};

// ============================================================================
// COMPLIANCE DOCUMENTS (platform tenant; super_admin acknowledgments)
// ============================================================================

export const complianceDocumentsService = {
  getAll: async () => {
    const response = await api.get('/admin/compliance-documents');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/compliance-documents/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/compliance-documents', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/compliance-documents/${id}`, data);
    return response.data;
  },

  publish: async (id) => {
    const response = await api.post(`/admin/compliance-documents/${id}/publish`);
    return response.data;
  },

  acknowledge: async (id, data) => {
    const response = await api.post(`/admin/compliance-documents/${id}/acknowledge`, data);
    return response.data;
  },

  listAcknowledgments: async (id) => {
    const response = await api.get(`/admin/compliance-documents/${id}/acknowledgments`);
    return response.data;
  },
};

// ============================================================================
// ONBOARDING
// ============================================================================

export const onboardingService = {
  // Create new tenant with initial setup
  createTenant: async (data) => {
    const response = await api.post('/admin/onboarding/tenant', data);
    return response.data;
  },

  // Create initial admin user for tenant
  createAdminUser: async (tenantId, userData) => {
    const response = await api.post(`/admin/onboarding/${tenantId}/admin-user`, userData);
    return response.data;
  },

  // Set up business configuration
  configureBusinessSettings: async (tenantId, settings) => {
    const response = await api.post(`/admin/onboarding/${tenantId}/business-settings`, settings);
    return response.data;
  },

  // Initialize chart of accounts
  initializeChartOfAccounts: async (tenantId, template) => {
    const response = await api.post(`/admin/onboarding/${tenantId}/chart-of-accounts`, { template });
    return response.data;
  },

  // Set up integrations
  configureIntegrations: async (tenantId, integrations) => {
    const response = await api.post(`/admin/onboarding/${tenantId}/integrations`, integrations);
    return response.data;
  },

  // Load sample/starter data
  loadSampleData: async (tenantId, options) => {
    const response = await api.post(`/admin/onboarding/${tenantId}/sample-data`, options);
    return response.data;
  },

  // Complete onboarding
  completeOnboarding: async (tenantId, options = {}) => {
    const response = await api.post(`/admin/onboarding/${tenantId}/complete`, options);
    return response.data;
  },

  // Get available COA templates
  getChartOfAccountsTemplates: async () => {
    const response = await api.get('/admin/onboarding/coa-templates');
    return response.data;
  },
};

// ============================================================================
// SYSTEM
// ============================================================================

export const systemService = {
  getDashboardStats: async () => {
    const response = await api.get('/admin/system/dashboard');
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get('/admin/system/settings');
    return response.data;
  },

  updateSettings: async (settings) => {
    const response = await api.put('/admin/system/settings', settings);
    return response.data;
  },

  getAuditLog: async (params = {}) => {
    const response = await api.get('/admin/system/audit-log', { params });
    return response.data;
  },

  getHealthCheck: async () => {
    const response = await api.get('/admin/system/health');
    return response.data;
  },

  getGithubScorecard: async () => {
    const response = await api.get('/admin/github-scorecard');
    return response.data;
  },

  getComplianceSummary: async () => {
    const response = await api.get('/admin/compliance-reports/summary');
    return response.data;
  },

  getComplianceReport: async (name) => {
    const response = await api.get(`/admin/compliance-reports/${name}`);
    return response.data;
  },

  downloadComplianceReport: async (name, fallbackFilename) => {
    const response = await api.get(`/admin/compliance-reports/${name}`, {
      params: { format: 'csv' },
      responseType: 'blob',
    });
    const match = /filename="([^"]+)"/.exec(response.headers['content-disposition'] || '');
    const filename = match ? match[1] : fallbackFilename;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

// ============================================================================
// INTEGRATIONS
// ============================================================================

export const integrationsService = {
  // Stripe
  getStripeStatus: async (tenantId) => {
    const response = await api.get(`/admin/integrations/${tenantId}/stripe/status`);
    return response.data;
  },

  configureStripe: async (tenantId, config) => {
    const response = await api.post(`/admin/integrations/${tenantId}/stripe`, config);
    return response.data;
  },

  // Plaid
  getPlaidStatus: async (tenantId) => {
    const response = await api.get(`/admin/integrations/${tenantId}/plaid/status`);
    return response.data;
  },

  configurePlaid: async (tenantId, config) => {
    const response = await api.post(`/admin/integrations/${tenantId}/plaid`, config);
    return response.data;
  },
};

// ============================================================================
// SUBSCRIPTIONS
// ============================================================================

export const subscriptionsService = {
  // Get available subscription plans
  getPlans: async () => {
    const response = await api.get('/subscriptions/plans');
    return response.data;
  },

  // Get a specific plan
  getPlan: async (slug) => {
    const response = await api.get(`/subscriptions/plans/${slug}`);
    return response.data;
  },

  // Create setup intent for collecting payment method
  createSetupIntent: async (tenantId, customerEmail, customerName) => {
    const response = await api.post('/subscriptions/create-setup-intent', {
      tenant_id: tenantId,
      customer_email: customerEmail,
      customer_name: customerName
    });
    return response.data;
  },

  // Create subscription with payment method
  createSubscription: async (data) => {
    const response = await api.post('/subscriptions/create', data);
    return response.data;
  },

  // Create trial subscription (no payment method required)
  createTrialSubscription: async (data) => {
    const response = await api.post('/subscriptions/create-trial', data);
    return response.data;
  },

  // Get tenant's subscription status
  getTenantSubscription: async (tenantId) => {
    const response = await api.get(`/subscriptions/tenant/${tenantId}`);
    return response.data;
  },

  // Skip subscription (for internal/test tenants)
  skipSubscription: async (tenantId) => {
    const response = await api.post('/subscriptions/skip', { tenant_id: tenantId });
    return response.data;
  },

  // Preview a cash/send-invoice subscription's first invoice WITHOUT creating it
  previewCashSubscription: async (data) => {
    const response = await api.post('/subscriptions/preview-cash', data);
    return response.data;
  },

  // Create a cash/send-invoice subscription (no card; Stripe issues the invoice)
  createCashSubscription: async (data) => {
    const response = await api.post('/subscriptions/create-cash', data);
    return response.data;
  },

  // Record an out-of-band (cash/check) payment against a Stripe invoice
  recordCashPayment: async (data) => {
    const response = await api.post('/subscriptions/record-cash-payment', data);
    return response.data;
  },

  finalizeCashInvoice: async (data) => {
    const response = await api.post('/subscriptions/finalize-cash-invoice', data);
    return response.data;
  },

  sendCashInvoice: async (data) => {
    const response = await api.post('/subscriptions/send-cash-invoice', data);
    return response.data;
  },

  // List all Stripe coupons
  getCoupons: async () => {
    const response = await api.get('/subscriptions/coupons');
    return response.data;
  },

  // Create a new Stripe coupon
  createCoupon: async (data) => {
    const response = await api.post('/subscriptions/coupons', data);
    return response.data;
  },
};

// ============================================================================
// SYSTEM SETTINGS (platform-wide; super_admin only)
// ============================================================================

export const roadmapService = {
  getBoard: async () => {
    const response = await api.get('/admin/roadmap');
    return response.data;
  },
  getItem: async (id) => {
    const response = await api.get(`/admin/roadmap/items/${id}`);
    return response.data;
  },
  createItem: async (body) => {
    const response = await api.post('/admin/roadmap/items', body);
    return response.data;
  },
  updateItem: async (id, body) => {
    const response = await api.patch(`/admin/roadmap/items/${id}`, body);
    return response.data;
  },
  moveItem: async (id, body) => {
    const response = await api.post(`/admin/roadmap/items/${id}/move`, body);
    return response.data;
  },
  deleteItem: async (id) => {
    const response = await api.delete(`/admin/roadmap/items/${id}`);
    return response.data;
  },
  updateDomain: async (slug, body) => {
    const response = await api.patch(`/admin/roadmap/domains/${slug}`, body);
    return response.data;
  },
  updatePathAhead: async (text) => {
    const response = await api.put('/admin/roadmap/path-ahead', { text });
    return response.data;
  },
  exportMarkdown: async () => {
    const response = await api.get('/admin/roadmap/export.md', {
      responseType: 'text',
    });
    return response.data;
  },
};

export const systemSettingsService = {
  // Get all platform settings (tenant_ref values include resolved_tenant)
  getAll: async () => {
    const response = await api.get('/system-settings');
    return response.data;
  },

  // Get one setting by key
  get: async (key) => {
    const response = await api.get(`/system-settings/${key}`);
    return response.data;
  },

  // Update one setting's value (null clears it)
  update: async (key, value) => {
    const response = await api.put(`/system-settings/${key}`, { value });
    return response.data;
  },
};

// ============================================================================
// REPORT ENGINE (Custom Report Engine authoring — super_admin)
// ============================================================================
// targetTenantId is the tenant a report is authored FOR: a tenant UUID, or null
// for a GLOBAL (all-tenant) report.
//   - WRITES (create/update) send the target in the BODY as tenant_id (authoring
//     is consent-free; the backend resolveWriteTenant reads the body). null =
//     global.
//   - READS (list/get) send the target via the X-Tenant-ID header so a
//     super_admin sees that tenant's specs + globals (backend getTenantId reads
//     the header for super_admins). For global-only browsing pass null (no
//     header) — the backend returns the platform tenant's specs + globals.
//   - RUN + dimension-values send the header (consent-gated paths).
//
// URLs are built with string concatenation (not template literals) on purpose.

function xt(targetTenantId) {
  // Per-request config: target-tenant header (reads/run/dimension-values).
  // Omit the header entirely for global (null) so the backend doesn't treat an
  // empty string as a target.
  return targetTenantId ? { headers: { 'X-Tenant-ID': targetTenantId } } : {};
}

export const reportEngineService = {
  listSpecs: async (targetTenantId, archetype) => {
    const cfg = xt(targetTenantId);
    if (archetype) cfg.params = { archetype };
    const response = await api.get('/report-engine/specs', cfg);
    return response.data;
  },

  getSpec: async (targetTenantId, id) => {
    const response = await api.get('/report-engine/specs/' + id, xt(targetTenantId));
    return response.data;
  },

  // WRITE: target goes in the BODY as tenant_id (UUID, or null = global).
  createSpec: async (targetTenantId, payload) => {
    const body = { ...payload, tenant_id: targetTenantId ?? null };
    const response = await api.post('/report-engine/specs', body);
    return response.data;
  },

  updateSpec: async (targetTenantId, id, payload) => {
    // tenant_id is not re-sent on update (a spec's scope is fixed at create);
    // the backend scopes the update by the existing row.
    const response = await api.put('/report-engine/specs/' + id, payload, xt(targetTenantId));
    return response.data;
  },

  deleteSpec: async (targetTenantId, id) => {
    const response = await api.delete('/report-engine/specs/' + id, xt(targetTenantId));
    return response.data;
  },

  runSpec: async (targetTenantId, id, runtimeParams) => {
    const cfg = xt(targetTenantId);
    if (runtimeParams) cfg.params = runtimeParams;
    const response = await api.get('/report-engine/specs/' + id + '/run', cfg);
    return response.data;
  },

  // Cross-tenant troubleshooting list (super_admin). Returns EVERY spec across
  // all tenants, each with tenant_name (null = Global). No target header needed
  // — it is a super_admin platform-wide call, not consent-gated (metadata only).
  listAllSpecs: async () => {
    const response = await api.get('/report-engine/specs-all');
    return response.data;
  },

  cloneSpec: async (sourceId, payload) => {
    const response = await api.post('/report-engine/specs/' + sourceId + '/clone', payload);
    return response.data;
  },
};

// Market Manager helpers used by report authoring previews. listEvents returns
// the target tenant's market events (id + title + date) so the pivot preview can
// offer an event dropdown instead of a raw numeric id. Hits the report-engine
// CATALOG endpoint (not /market/events) because only the catalog router honors
// the super_admin X-Tenant-ID target + consent gate; the market router resolves
// to the caller's own tenant and would return the platform tenant's (empty) set.
export const marketService = {
  listEvents: async (targetTenantId) => {
    const response = await api.get('/report-engine/catalog/market-events', xt(targetTenantId));
    return response.data;
  },
};

export const reportCatalogService = {
  getArchetypes: async (targetTenantId) => {
    const response = await api.get('/report-engine/catalog/archetypes', xt(targetTenantId));
    return response.data;
  },

  getOperators: async (targetTenantId) => {
    const response = await api.get('/report-engine/catalog/operators', xt(targetTenantId));
    return response.data;
  },

  getRecords: async (targetTenantId, hierarchyCapable) => {
    const cfg = xt(targetTenantId);
    if (hierarchyCapable) cfg.params = { hierarchyCapable: true };
    const response = await api.get('/report-engine/catalog/records', cfg);
    return response.data;
  },

  getFields: async (targetTenantId, recordName) => {
    const response = await api.get('/report-engine/catalog/records/' + encodeURIComponent(recordName) + '/fields', xt(targetTenantId));
    return response.data;
  },

  getGrains: async (targetTenantId) => {
    const response = await api.get('/report-engine/catalog/grains', xt(targetTenantId));
    return response.data;
  },

  getDimensionValues: async (targetTenantId, grainName) => {
    const response = await api.get('/report-engine/catalog/grains/' + encodeURIComponent(grainName) + '/dimension-values', xt(targetTenantId));
    return response.data;
  },

  getDetailChildren: async (targetTenantId, recordName) => {
    const response = await api.get('/report-engine/catalog/detail-children/' + encodeURIComponent(recordName), xt(targetTenantId));
    return response.data;
  },

  getReportTypes: async (targetTenantId) => {
    const response = await api.get('/report-engine/catalog/report-types', xt(targetTenantId));
    return response.data;
  },

  getReportType: async (targetTenantId, typeId) => {
    const response = await api.get('/report-engine/catalog/report-types/' + encodeURIComponent(typeId), xt(targetTenantId));
    return response.data;
  },

  getMarketVendors: async (targetTenantId) => {
    const response = await api.get('/report-engine/catalog/market-vendors', xt(targetTenantId));
    return response.data;
  },
};

export default api;
